import {Button, Icon, Row, Col, Space, Label, Text, Modal} from '@mc/ui-v4';
import React, {useEffect, useRef, useState} from 'react';
import './LayerSelector.scss';
import classNames from 'classnames';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {LayerSettingsApi, LayerVO, TopicVO} from '../../../api';
import {useAppConfig} from '@mc/react-app-base';
import {mcAirplane, mcClose, mcColor, mcLayers, mcSortAsc, mcSortDesc, mcWithoutContent} from '@mc/react-ui-icons';
import {LayerSelectorModal} from '../../modals/ConfigurationModal/LayerSelectorModal';
import {CACHE_KEY_TOPICS_MAP} from '../../../hooks/useTopicsMap';
import {rffFormSubmit} from '@mc/react-app-v4';
import {QueryClient, useQueryClient} from 'react-query';
import {useIsMobile} from '../../../hooks/useIsMobile';
import {BottomSheet} from '../../mobile/BottomSheet';
import {LayerId} from '@mc/react-mapio';
import {truncate} from '../../../helpers/DataMutators';

type Props = {
    topic: TopicVO;
    serverPublic: boolean;
    backgroundLayerId: LayerId;
    foregroundLayerId: LayerId;
    backgroundLayers: LayerVO[];
    foregroundLayers: LayerVO[];
    isAllowedToEdit?: boolean;
    onSetLayer?: (selectionType: LayerSelectionType, id: LayerId) => void;
    onClose?: () => void;
    disabled?: boolean;
    flat?: boolean;
};

export enum LayerSelectionType {
    BACKGROUND_LAYER,
    FOREGROUND_LAYER,
}

export const LayerSelector: React.FC<Props> = ({
    topic,
    serverPublic,
    backgroundLayerId,
    foregroundLayerId,
    backgroundLayers,
    foregroundLayers,
    isAllowedToEdit = false,
    onSetLayer = null,
    onClose = null,
    disabled,
    flat,
}) => {
    const appConfig = useAppConfig();
    const queryClient: QueryClient = useQueryClient();
    const isMobile = useIsMobile();

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const layerSelectorRef = useRef(null);

    const selectedBgLayer = backgroundLayers ? backgroundLayers.find(x => x.id === backgroundLayerId) : undefined;

    const handleLayerSelect = (type: LayerSelectionType, id: number) => {
        onSetLayer?.(type, id);
    };

    useEffect(() => {
        const iframes = document.querySelectorAll('iframe');
        if (iframes) {
            if (isOpen) {
                iframes.forEach(iframe => {
                    iframe.classList.add('no-pointer-events');
                });
            } else {
                iframes.forEach(iframe => {
                    iframe.classList.remove('no-pointer-events');
                });
            }
        }
    }, [isOpen]);
    useEffect(() => {
        function handleClickOutside(event) {
            if (layerSelectorRef.current && !layerSelectorRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const openLayerSelectorModal = (layerType: LayerSelectionType): void => {
        Modal.show(({onClose}) => (
            <LayerSelectorModal
                onSubmit={async data => {
                    return await rffFormSubmit(async () => {
                        let layers = [];
                        for (const entry of Object.entries(data)) {
                            if (entry[1]) {
                                layers.push(entry[0].split('#').join('.'));
                            }
                        }
                        if (layerType === LayerSelectionType.BACKGROUND_LAYER) {
                            topic.backgroundLayers = layers;
                        } else if (layerType === LayerSelectionType.FOREGROUND_LAYER) {
                            topic.foregroundLayers = layers;
                        }
                        await LayerSettingsApi.updateTopicSettings(topic.id, topic);
                        await queryClient.invalidateQueries([CACHE_KEY_TOPICS_MAP]);
                        onClose();
                    });
                }}
                onClose={onClose}
                topic={topic}
                layerType={layerType}
            />
        ));
    };

    const layerRenderer = (layers: LayerVO[], type: LayerSelectionType, selectedId: LayerId) => {
        return (
            <>
                {layers.map((l, i) => (
                    <div
                        key={i}
                        className={classNames('layer-wrapper', selectedId === l.id ? 'selected' : false)}
                        onClick={() => handleLayerSelect(type, l.id)}
                    >
                        <img src={appConfig.serverContextPath + '/api/files/img/' + l.layerImage} alt={l.label} />
                        <Text className={'mt-1'} intent={selectedId === l.id ? 'primary' : 'default'}>
                            {truncate(l.label.replace(/_/g, ' '), 25)}
                        </Text>
                    </div>
                ))}
            </>
        );
    };
    const flatLayerRenderer = (layers: LayerVO[], type: LayerSelectionType, selectedId: LayerId) => {
        return (
            <>
                {layers.map((l, i) => (
                    <div
                        style={{width: '68px', minHeight: '69px'}}
                        key={i}
                        className={classNames('layer-wrapper d-flex flex-column', selectedId === l.id ? 'selected' : false, {
                            disabled,
                            'disabled-layer': disabled,
                        })}
                        onClick={() => !disabled && handleLayerSelect(type, l.id)}
                    >
                        <img src={appConfig.serverContextPath + '/api/files/img/' + l.layerImage} alt={l.label} />

                        <Text className={'mt-1'} intent={selectedId === l.id ? 'primary' : 'default'}>
                            {truncate(l.label.replace(/_/g, ' '), 25)}
                        </Text>
                    </div>
                ))}
            </>
        );
    };
    const noLayerRenderer = (type: LayerSelectionType, caption: string, selectedId: LayerId) => {
        return (
            <div
                className={classNames('layer-wrapper', !!!selectedId ? 'selected' : false, {
                    disabled,
                    'disabled-layer': disabled,
                })}
                onClick={() => !disabled && handleLayerSelect(type, null)}
            >
                <div className={'no-layer'}>
                    <Icon icon={mcWithoutContent} />
                </div>
                <Text className={'mt-1'} intent={selectedId ? 'default' : 'primary'}>
                    {caption}
                </Text>
            </div>
        );
    };

    const getButtonIcon = (layer: LayerVO): IconDefinition => {
        if (!layer) {
            if (isMobile && serverPublic) {
                return mcLayers;
            }
            return mcWithoutContent;
        }

        return layer.aerial ? mcAirplane : mcColor;
    };

    if (flat && (backgroundLayers || foregroundLayers)) {
        return (
            <div className={'layer-selector'}>
                <Row className={'section-wrapper horizontal'}>
                    {backgroundLayers && (
                        <Col>
                            <Label className={'mb-1'}>
                                Podkladová mapa
                                {isAllowedToEdit && (
                                    <Button
                                        style={'link'}
                                        className={'d-inline-block ml-2'}
                                        disabled={disabled}
                                        onClick={() => openLayerSelectorModal(LayerSelectionType.BACKGROUND_LAYER)}
                                    >
                                        Omezit výběr
                                    </Button>
                                )}
                            </Label>
                            <Space className={'section'} wrap>
                                {flatLayerRenderer(backgroundLayers, LayerSelectionType.BACKGROUND_LAYER, backgroundLayerId)}
                                {noLayerRenderer(LayerSelectionType.BACKGROUND_LAYER, 'Žádná', backgroundLayerId)}
                            </Space>
                        </Col>
                    )}
                    {foregroundLayers && (
                        <Col>
                            <Label className={'mb-1'}>
                                Popisná mapa
                                {isAllowedToEdit && (
                                    <Button
                                        style={'link'}
                                        className={'d-inline-block ml-2'}
                                        disabled={disabled}
                                        onClick={() => openLayerSelectorModal(LayerSelectionType.FOREGROUND_LAYER)}
                                    >
                                        Omezit výběr
                                    </Button>
                                )}
                            </Label>
                            <Space className={'section'} wrap>
                                {noLayerRenderer(LayerSelectionType.FOREGROUND_LAYER, 'Základní mapa', foregroundLayerId)}
                                {flatLayerRenderer(foregroundLayers, LayerSelectionType.FOREGROUND_LAYER, foregroundLayerId)}
                            </Space>
                        </Col>
                    )}
                </Row>
            </div>
        );
    }

    const isMobileSheet = isMobile && serverPublic;
    const layerSelectorContent = (
        <>
            {backgroundLayers && (
                <div className={'section-wrapper'}>
                    <div className={'header mb-1'}>
                        <h4>Podkladová mapa</h4>
                        {!isMobileSheet && <Button style="link" disabled={disabled} onClick={() => setIsOpen(false)} icon={<Icon icon={mcClose} />} />}
                    </div>
                    <div className={'section'}>
                        {layerRenderer(backgroundLayers, LayerSelectionType.BACKGROUND_LAYER, backgroundLayerId)}
                        {noLayerRenderer(LayerSelectionType.BACKGROUND_LAYER, 'Žádná', backgroundLayerId)}
                    </div>
                </div>
            )}
            {foregroundLayers && backgroundLayers && <div className={'separator'}></div>}
            {foregroundLayers && (
                <div className={'section-wrapper'}>
                    <div className={'header mb-2'}>
                        <h4>Doplňující informace</h4>
                    </div>
                    <div className={'section'}>
                        {noLayerRenderer(LayerSelectionType.FOREGROUND_LAYER, 'Základní mapa', foregroundLayerId)}
                        {layerRenderer(foregroundLayers, LayerSelectionType.FOREGROUND_LAYER, foregroundLayerId)}
                    </div>
                </div>
            )}
        </>
    );

    return (
        <div className={'layer-selector-wrapper'}>
            {isMobile && serverPublic ? (
                <Button
                    className={`drop-shadow ${isOpen ? 'active' : ''}`}
                    style={'link'}
                    noFocus
                    disabled={disabled}
                    onClick={() => setIsOpen(!isOpen)}
                    title={isOpen ? false : 'Změna podkladové a popisné mapy'}
                    icon={<Icon intent={'primary'} icon={mcLayers} />}
                />
            ) : (
                <Button
                    className={'drop-shadow px-2'}
                    noFocus
                    disabled={disabled}
                    onClick={() => setIsOpen(!isOpen)}
                    icon={<Icon icon={getButtonIcon(selectedBgLayer)} />}
                    title={isOpen ? false : 'Změna podkladové a popisné mapy'}
                    iconRight={<Icon icon={isOpen ? mcSortAsc : mcSortDesc} />}
                >
                    {selectedBgLayer?.label ?? 'Žádná mapa'}
                </Button>
            )}
            {isOpen &&
                (backgroundLayers || foregroundLayers) &&
                (isMobile && serverPublic ? (
                    <BottomSheet open onClose={() => setIsOpen(false)} title={'Mapové podklady'} snapPoints={[55, 92]} defaultSnapPointIndex={0} className={'layer-selector-sheet'}>
                        <div className={'layer-selector'}>{layerSelectorContent}</div>
                    </BottomSheet>
                ) : (
                    <div ref={layerSelectorRef} className={'layer-selector tool-popover drop-shadow'}>
                        {layerSelectorContent}
                    </div>
                ))}
        </div>
    );
};
